import './index.css';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { LayoutShell } from './App.tsx';
import { ToastProvider } from './components/Toast.tsx';
import { I18nProvider } from './lib/i18n.ts';
import { ModeProvider } from './lib/theme-mode.ts';

// ── Standalone pages (no Layout wrapper) ───────────────────────────────────
const Login = lazy(() => import('./pages/Login.tsx'));
const Abuse = lazy(() => import('./pages/Abuse.tsx'));

// ── Home — Layout-wrapped (header/nav/footer) ───────────────────────────────
const Home = lazy(() => import('./pages/Home.tsx'));

// ── Layout-wrapped pages ────────────────────────────────────────────────────
const Inbox = lazy(() => import('./pages/Inbox.tsx'));
const Message = lazy(() => import('./pages/Message.tsx'));
const Account = lazy(() => import('./pages/Account.tsx'));
const ApiKeys = lazy(() => import('./pages/ApiKeys.tsx'));
const Webhooks = lazy(() => import('./pages/Webhooks.tsx'));
const Admin = lazy(() => import('./pages/Admin.tsx'));
const AdminUsers = lazy(() => import('./pages/admin/Users.tsx'));
const AdminAudit = lazy(() => import('./pages/admin/Audit.tsx'));
const AdminAddresses = lazy(() => import('./pages/admin/Addresses.tsx'));
const AdminDomains = lazy(() => import('./pages/admin/Domains.tsx'));
const AdminBlocklist = lazy(() => import('./pages/admin/Blocklist.tsx'));
const AdminAbuse = lazy(() => import('./pages/admin/Abuse.tsx'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 1000 * 5, retry: 1 },
  },
});

function PageFallback() {
  // Minimal top-bar progress spinner shown during lazy page loads
  return (
    <div className="fixed top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-cyan-500 to-blue-600 animate-pulse" />
  );
}

function lazyRoute(Element: React.LazyExoticComponent<React.ComponentType>) {
  return (
    <Suspense fallback={<PageFallback />}>
      <Element />
    </Suspense>
  );
}

const router = createBrowserRouter([
  // ── Standalone routes (no shared Layout) ──────────────────────────────
  { path: '/login', element: lazyRoute(Login) },
  { path: '/abuse', element: lazyRoute(Abuse) },

  // ── Layout-wrapped routes ──────────────────────────────────────────────
  {
    element: <LayoutShell />,
    children: [
      { path: '/', element: lazyRoute(Home) },
      { path: '/inbox/:id', element: lazyRoute(Inbox) },
      { path: '/inbox/:id/message/:mid', element: lazyRoute(Message) },
      { path: '/account', element: lazyRoute(Account) },
      { path: '/account/api-keys', element: lazyRoute(ApiKeys) },
      { path: '/account/webhooks', element: lazyRoute(Webhooks) },
      {
        path: '/admin',
        element: lazyRoute(Admin),
        children: [
          { index: true, element: lazyRoute(AdminUsers) },
          { path: 'users', element: lazyRoute(AdminUsers) },
          { path: 'addresses', element: lazyRoute(AdminAddresses) },
          { path: 'domains', element: lazyRoute(AdminDomains) },
          { path: 'abuse', element: lazyRoute(AdminAbuse) },
          { path: 'blocklist', element: lazyRoute(AdminBlocklist) },
          { path: 'audit', element: lazyRoute(AdminAudit) },
        ],
      },
    ],
  },
]);

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Missing #root element');

createRoot(rootEl).render(
  <StrictMode>
    <ModeProvider>
      <I18nProvider>
        <QueryClientProvider client={queryClient}>
          <ToastProvider>
            <RouterProvider router={router} />
          </ToastProvider>
        </QueryClientProvider>
      </I18nProvider>
    </ModeProvider>
  </StrictMode>,
);
